import React from 'react';
import { 
  FileText, 
  Users, 
  Search, 
  Plus, 
  FolderOpen,
  Inbox,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

/**
 * EmptyState - Componente para mostrar estados vacíos con acción sugerida
 * 
 * @param {string} title - Título del estado vacío
 * @param {string} description - Descripción explicativa
 * @param {React.Component} icon - Icono de Lucide (opcional)
 * @param {string} actionLabel - Texto del botón de acción (opcional)
 * @param {function} onAction - Función al hacer clic en el botón (opcional)
 * @param {string} actionVariant - 'primary' | 'secondary' (default: 'primary')
 * @param {string} type - Tipo predefinido: 'search', 'list', 'error', 'loading'
 */
export const EmptyState = ({
  title,
  description,
  icon: CustomIcon,
  actionLabel,
  onAction,
  actionVariant = 'primary',
  type,
  className = ''
}) => {
  // Configuraciones predefinidas
  const presets = {
    search: {
      icon: Search,
      title: 'No se encontraron resultados',
      description: 'Intenta con otros términos de búsqueda o ajusta los filtros.',
      actionLabel: 'Limpiar filtros'
    },
    list: {
      icon: FolderOpen,
      title: 'No hay registros',
      description: 'Aún no hay elementos para mostrar en esta lista.',
      actionLabel: 'Crear nuevo'
    },
    fichas: {
      icon: FileText,
      title: 'No hay fichas',
      description: 'Aún no has creado ninguna ficha de medidas.',
      actionLabel: 'Nueva ficha'
    },
    clientes: {
      icon: Users,
      title: 'No hay clientes',
      description: 'Aún no tienes clientes registrados en el sistema.',
      actionLabel: 'Registrar cliente'
    },
    cotizaciones: {
      icon: FileText,
      title: 'No hay cotizaciones',
      description: 'Aún no has creado ninguna cotización.',
      actionLabel: 'Nueva cotización'
    },
    error: {
      icon: AlertCircle,
      title: 'Ocurrió un error',
      description: 'No pudimos cargar la información. Por favor intenta nuevamente.',
      actionLabel: 'Reintentar'
    },
    inbox: {
      icon: Inbox,
      title: 'Bandeja vacía',
      description: 'No tienes mensajes ni notificaciones pendientes.'
    }
  };

  const preset = type ? presets[type] : null;
  const Icon = CustomIcon || preset?.icon || FolderOpen;
  const finalTitle = title || preset?.title || 'No hay datos';
  const finalDescription = description || preset?.description;
  const finalActionLabel = actionLabel || preset?.actionLabel;

  const buttonStyles = {
    primary: 'bg-slate-900 text-white hover:bg-slate-800 shadow-sm',
    secondary: 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
  };

  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 ${className}`}>
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
      </div>
      
      <h3 className="text-lg font-medium text-slate-900 text-center mb-2">
        {finalTitle}
      </h3>
      
      {finalDescription && (
        <p className="text-sm text-slate-500 text-center max-w-sm mb-6">
          {finalDescription}
        </p>
      )}
      
      {finalActionLabel && onAction && (
        <button
          onClick={onAction}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${buttonStyles[actionVariant]}`}
          data-testid="empty-state-action"
        >
          {type === 'error' ? <RefreshCw className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {finalActionLabel}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
